import React, { useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AIAgentHeader from './components/AIAgentHeader';
import AIAgentMessageList from './components/AIAgentMessageList';
import AIAgentInitialSuggestions from './components/AIAgentInitialSuggestions';
import AIAgentComposer from './components/AIAgentComposer';
import AIAgentStatusMessage from './components/AIAgentStatusMessage';
import {
  AI_AGENT_PANEL_WIDTH_EXPANDED,
  AI_AGENT_PANEL_WIDTH_NORMAL,
  AI_AGENT_TOKENS,
} from './AIAgentConstants';
import { AIAgentConversation } from './AIAgentTypes';
import { canResetConversation, canSendMessage, isInitialState } from './AIAgentReducer';
import {
  CONVERSATION_LIMIT_MESSAGE,
  INVALID_USER_MESSAGE,
} from './AIAgentErrorMapper';

interface Props {
  state: AIAgentConversation;
  onClose: () => void;
  onExpand: () => void;
  onReturnNormal: () => void;
  onStartNewConversation: () => void;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onSuggestionSelect: (text: string) => void;
  onRetry: (questionId: string) => void;
  restoreFocusRef?: React.RefObject<HTMLElement>;
}

export default function AIAgentDrawer({
  state,
  onClose,
  onExpand,
  onReturnNormal,
  onStartNewConversation,
  onDraftChange,
  onSend,
  onSuggestionSelect,
  onRetry,
  restoreFocusRef,
}: Props) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const open = state.mode !== 'closed';
  const panelWidth =
    state.mode === 'expanded'
      ? AI_AGENT_PANEL_WIDTH_EXPANDED
      : AI_AGENT_PANEL_WIDTH_NORMAL;

  const showInitialSuggestions = isInitialState(state);
  const showComposer =
    state.availability === 'active' && state.identityStatus === 'valid';
  const showConversationLimit =
    state.availability === 'conversation-limit';
  const hasConversationLimitMessage = state.messages.some(
    (message) => message.action === 'start-new-conversation'
  );
  const showInvalidIdentity =
    state.identityStatus === 'invalid' && state.mode !== 'closed';
  const controlsDisabled =
    state.phase === 'resetting' || state.identityStatus === 'resolving';

  const wasOpenRef = useRef(open);

  useEffect(() => {
    if (wasOpenRef.current && !open && restoreFocusRef?.current) {
      restoreFocusRef.current.focus();
    }
    wasOpenRef.current = open;
  }, [open, restoreFocusRef]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      data-testid="ai-agent-drawer"
      aria-label="Assistente SGD"
      sx={{
        zIndex: (t) => t.zIndex.modal + 1,
        '& .MuiDrawer-paper': {
          width: isXs ? '100vw' : `min(${panelWidth}px, 100vw)`,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: AI_AGENT_TOKENS.surface,
          fontFamily: AI_AGENT_TOKENS.fontFamily,
        },
      }}
      PaperProps={{
        'data-panel-width': String(panelWidth),
      }}
    >
      <AIAgentHeader
        mode={state.mode}
        onClose={onClose}
        onExpand={onExpand}
        onReturnNormal={onReturnNormal}
        onStartNewConversation={onStartNewConversation}
        disableControls={!canResetConversation(state) || controlsDisabled}
      />

      <AIAgentStatusMessage
        notice={state.notice}
        onStartNewConversation={onStartNewConversation}
      />

      {showInvalidIdentity && (
        <Box
          sx={{
            px: 2,
            py: 2,
            backgroundColor: AI_AGENT_TOKENS.surface,
          }}
          role="status"
        >
          {INVALID_USER_MESSAGE}
        </Box>
      )}

      {showInitialSuggestions ? (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            backgroundColor: AI_AGENT_TOKENS.surface,
          }}
        >
          <AIAgentInitialSuggestions
            onSelect={onSuggestionSelect}
            disabled={!canSendMessage(state)}
          />
        </Box>
      ) : (
        <AIAgentMessageList
          messages={state.messages}
          onRetry={onRetry}
          onStartNewConversation={onStartNewConversation}
          autoScroll={state.phase === 'sending' || state.phase === 'resetting'}
        />
      )}

      {state.phase === 'resetting' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} aria-label="Reiniciando conversa" />
        </Box>
      )}

      {showConversationLimit ? (
        <Box sx={{ px: 2, py: 2, backgroundColor: AI_AGENT_TOKENS.white }}>
          {!hasConversationLimitMessage && (
            <AIAgentStatusMessage
              notice={{
                kind: 'conversation-limit',
                message: CONVERSATION_LIMIT_MESSAGE,
                action: 'start-new-conversation',
              }}
              onStartNewConversation={onStartNewConversation}
            />
          )}
        </Box>
      ) : (
        showComposer && (
          <AIAgentComposer
            draft={state.draft}
            onDraftChange={onDraftChange}
            onSend={onSend}
            disabled={!canSendMessage(state) || state.phase === 'sending'}
            showDisclaimer
          />
        )
      )}
    </Drawer>
  );
}
